import React, { useState, useEffect, useRef, useCallback } from 'react';
import { subscribeRoom } from '../shared/roomTx';
import { handleChainTimeout, submitChainAnswer } from '../shared/chainRoomTx';
import { getRemainingTime, getPlayerBySlot, CHAIN_RESULT } from '../shared/chainLogic';
import {
  FL_ROOM_STATUS,
  CHAIN_PHASE,
  FL_GAME_MODE,
} from '../shared/fiveLeague';
import {
  testTTS,
  readChainQuestion,
  announceChainCorrect,
  announceChainWrong,
  announceChainTimeout,
  announceChainPerfect,
} from '../shared/tts';
import { isTTSEnabled, setTTSEnabled, hasTTSPreference } from '../shared/ttsSettings';
import VoiceInput, { isVoiceInputSupported } from '../components/VoiceInput';
import './ChainTV.css';

// 順番決めタイマーコンポーネント
function OrderTimer({ deadline }) {
  const [remaining, setRemaining] = React.useState(0);

  React.useEffect(() => {
    const updateTimer = () => {
      const now = Date.now();
      const remaining = Math.max(0, Math.ceil((deadline - now) / 1000));
      setRemaining(remaining);
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [deadline]);

  return (
    <div className="tv-order-timer">
      <span className={`timer-value ${remaining <= 10 ? 'warning' : ''}`}>
        {remaining}
      </span>
      <span className="timer-label">秒</span>
    </div>
  );
}

function ChainTV({ roomId }) {
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(true);
  const [remainingTime, setRemainingTime] = useState(0);
  const [showCorrect, setShowCorrect] = useState(false);
  const [showWrong, setShowWrong] = useState(false);
  const [meteorPosition, setMeteorPosition] = useState(0); // 0-100%で落下位置

  // TTS関連
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const pref = isTTSEnabled();
    return pref !== false;
  });
  const [showSoundPrompt, setShowSoundPrompt] = useState(!hasTTSPreference());
  const [ttsReady, setTtsReady] = useState(false);

  // 音声入力関連
  const [micEnabled, setMicEnabled] = useState(false); // マイク有効化フラグ
  const [isSpeaking, setIsSpeaking] = useState(false); // TTS読み上げ中フラグ
  const [voiceInterim, setVoiceInterim] = useState(''); // 認識中テキスト
  const [submitting, setSubmitting] = useState(false); // 回答送信中
  const voiceSupported = isVoiceInputSupported();

  const timerRef = useRef(null);
  const prevPhaseRef = useRef(null);
  const phaseHandledRef = useRef({});

  // 音声認識結果を処理して回答送信
  const handleVoiceResult = useCallback(async (text) => {
    if (!room?.game || submitting) return;
    if (room.game.phase !== CHAIN_PHASE.ANSWERING) return;

    const currentPlayerId = room.playerOrder[room.game.currentPlayerSlot];
    if (!currentPlayerId) return;

    console.log('[ChainTV] Voice result:', text, 'for player:', currentPlayerId);
    setSubmitting(true);
    setVoiceInterim('');

    try {
      const result = await submitChainAnswer(roomId, currentPlayerId, text.trim());
      console.log('[ChainTV] Submit result:', result);
    } catch (err) {
      console.error('[ChainTV] Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  }, [room, roomId, submitting]);

  // 中間結果を表示
  const handleVoiceInterim = useCallback((text) => {
    setVoiceInterim(text);
  }, []);

  // TTS初期化（ユーザーインタラクション後）
  const initTTS = async () => {
    if (ttsReady) return;
    console.log('[ChainTV] Initializing TTS...');
    const success = await testTTS();
    setTtsReady(true);
    if (success) {
      console.log('[ChainTV] TTS ready!');
    } else {
      console.warn('[ChainTV] TTS failed to initialize');
    }
  };

  // 音声ON/OFF切り替え
  const handleSoundToggle = async (enabled) => {
    setSoundEnabled(enabled);
    setTTSEnabled(enabled);
    setShowSoundPrompt(false);
    if (enabled && !ttsReady) {
      await initTTS();
    }
  };

  // ルーム購読
  useEffect(() => {
    if (!roomId) return;

    const unsub = subscribeRoom(roomId, (data) => {
      setLoading(false);
      setRoom(data);
    });

    return () => unsub();
  }, [roomId]);

  // タイマー更新 & 隕石位置計算
  const timeoutTriggeredRef = useRef(false);

  useEffect(() => {
    if (!room?.game?.endsAt) return;

    // フェーズが変わったらタイムアウトフラグをリセット
    timeoutTriggeredRef.current = false;

    // 総時間を計算（5問=30秒、10問=60秒）
    const answerCount = room.game?.answerCount || 5;
    const totalTime = answerCount === 10 ? 60 : 30;

    const updateTimer = () => {
      const remaining = getRemainingTime(room.game.endsAt);
      setRemainingTime(remaining);

      // 隕石の落下位置を計算 (0%=上, 100%=下)
      const fallPercent = Math.min(100, Math.max(0, ((totalTime - remaining) / totalTime) * 100));
      setMeteorPosition(fallPercent);

      // タイムアウト処理 - 0秒になったらゲームオーバー
      if (remaining === 0 && !timeoutTriggeredRef.current && room?.game?.phase === CHAIN_PHASE.ANSWERING) {
        timeoutTriggeredRef.current = true;
        handleChainTimeout(roomId);
      }
    };

    updateTimer();
    timerRef.current = setInterval(updateTimer, 100);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [room?.game?.endsAt, room?.game?.phase, room?.game?.answerCount, roomId]);

  // フェーズ変更によるアニメーション & TTS
  useEffect(() => {
    if (!room?.game) return;
    const { phase, prompt, answerCount, submittedAnswers } = room.game;
    const prevPhase = prevPhaseRef.current;
    const phaseKey = `${phase}-${submittedAnswers?.length || 0}`;

    // TTS読み上げ中はマイクをミュート
    const speakWithMute = (speakFn, ...args) => {
      setIsSpeaking(true);
      speakFn(...args);
      // TTS終了を検出（speechSynthesisのonendイベント）
      const checkSpeaking = setInterval(() => {
        if (!window.speechSynthesis.speaking) {
          clearInterval(checkSpeaking);
          setTimeout(() => setIsSpeaking(false), 300); // 少し余裕を持たせる
        }
      }, 100);
      // 最大5秒でタイムアウト
      setTimeout(() => {
        clearInterval(checkSpeaking);
        setIsSpeaking(false);
      }, 5000);
    };

    // 問題読み上げフェーズ
    if (phase === CHAIN_PHASE.READING && prevPhase !== CHAIN_PHASE.READING) {
      if (soundEnabled && ttsReady && !phaseHandledRef.current[phaseKey]) {
        phaseHandledRef.current[phaseKey] = true;
        speakWithMute(readChainQuestion, prompt, answerCount);
      }
    }

    // 正解
    if (phase === CHAIN_PHASE.CORRECT && prevPhase !== CHAIN_PHASE.CORRECT) {
      setShowCorrect(true);
      setTimeout(() => setShowCorrect(false), 1500);
      if (soundEnabled && ttsReady) {
        const lastAnswer = submittedAnswers?.[submittedAnswers.length - 1];
        if (lastAnswer) {
          speakWithMute(announceChainCorrect, lastAnswer.text);
        }
      }
    }

    // 不正解
    if (phase === CHAIN_PHASE.WRONG && prevPhase !== CHAIN_PHASE.WRONG) {
      setShowWrong(true);
      setTimeout(() => setShowWrong(false), 1500);
      if (soundEnabled && ttsReady) {
        speakWithMute(announceChainWrong);
      }
    }

    // タイムアップ
    if (phase === CHAIN_PHASE.TIMEOUT && prevPhase !== CHAIN_PHASE.TIMEOUT) {
      if (soundEnabled && ttsReady) {
        speakWithMute(announceChainTimeout);
      }
    }

    // パーフェクト
    if (phase === CHAIN_PHASE.COMPLETE && prevPhase !== CHAIN_PHASE.COMPLETE) {
      if (soundEnabled && ttsReady) {
        speakWithMute(announceChainPerfect);
      }
    }

    prevPhaseRef.current = phase;
  }, [room?.game?.phase, room?.game?.submittedAnswers?.length, soundEnabled, ttsReady]);

  if (loading) {
    return (
      <div className="chain-tv-container">
        <div className="chain-tv-loading">読み込み中...</div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="chain-tv-container">
        <div className="chain-tv-error">
          <p>ルームが見つかりません</p>
          <p className="chain-tv-hint">ルームコードを確認してください</p>
        </div>
      </div>
    );
  }

  // 音声設定プロンプト
  if (showSoundPrompt) {
    return (
      <div className="chain-tv-container" onClick={initTTS}>
        <div className="chain-tv-sound-prompt">
          <h1 className="sound-prompt-title">⚡ ANSWER CHAIN</h1>
          <p className="sound-prompt-text">音声を有効にしますか？</p>
          <div className="sound-prompt-buttons">
            <button
              className="sound-btn sound-on"
              onClick={(e) => { e.stopPropagation(); handleSoundToggle(true); }}
            >
              🔊 音声ON
            </button>
            <button
              className="sound-btn sound-off"
              onClick={(e) => { e.stopPropagation(); handleSoundToggle(false); }}
            >
              🔇 音声OFF
            </button>
          </div>
          <p className="sound-prompt-hint">
            テレビ番組のような演出で楽しめます
          </p>

          {/* マイク入力設定 */}
          {voiceSupported && (
            <div className="mic-prompt-section">
              <p className="mic-prompt-text">🎤 音声認識（みんなで話して回答）</p>
              <div className="sound-prompt-buttons">
                <button
                  className={`sound-btn ${micEnabled ? 'sound-on' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setMicEnabled(true); }}
                >
                  🎤 マイクON
                </button>
                <button
                  className={`sound-btn ${!micEnabled ? 'sound-off' : ''}`}
                  onClick={(e) => { e.stopPropagation(); setMicEnabled(false); }}
                >
                  ⌨️ マイクOFF
                </button>
              </div>
              <p className="mic-prompt-hint">
                ONにすると回答時間中、音声で回答できます
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ワンワードモードの場合はTVDisplayへリダイレクト案内
  if (room.gameMode !== FL_GAME_MODE.CHAIN) {
    return (
      <div className="chain-tv-container">
        <div className="chain-tv-error">
          <p>このルームはワンワードモードです</p>
          <p className="chain-tv-hint">TV表示を使用してください</p>
        </div>
      </div>
    );
  }

  const game = room.game;
  const controllers = room.players?.filter(p => p.role !== 'display') || [];

  // 順番決めフェーズ
  if (!game || game.phase === CHAIN_PHASE.ORDER) {
    const votes = room.orderVotes || {};
    const okCount = Object.values(votes).filter(v => v.ok).length;
    const totalPlayers = controllers.length;
    const orderDeadline = room.orderDeadline;

    // localOrderからプレイヤー情報を取得
    const orderedPlayers = (room.playerOrder || [])
      .map(pid => controllers.find(p => p.playerId === pid))
      .filter(Boolean);

    // 各ポジションを希望しているプレイヤーを集計
    const preferencesByPosition = {};
    orderedPlayers.forEach((player, idx) => {
      preferencesByPosition[idx] = [];
    });
    Object.entries(votes).forEach(([playerId, vote]) => {
      if (vote?.preference !== undefined && vote.preference !== null) {
        const player = controllers.find(p => p.playerId === playerId);
        if (player && preferencesByPosition[vote.preference]) {
          preferencesByPosition[vote.preference].push(player.nickname);
        }
      }
    });

    return (
      <div className="chain-tv-container order-phase">
        <div className="chain-tv-order">
          <h1 className="order-title">
            <span className="order-title-icon">⚡</span>
            ANSWER CHAIN
          </h1>
          <p className="order-subtitle">回答順を決めています</p>

          {/* タイマー */}
          {orderDeadline && (
            <OrderTimer deadline={orderDeadline} />
          )}

          {/* メインの順番表示エリア */}
          <div className="tv-order-main">
            {/* 順番スロット */}
            <div className="tv-order-slots">
              {orderedPlayers.map((player, index) => {
                const vote = votes[player.playerId];
                const wantsThis = vote?.preference === index;
                const hasOk = vote?.ok;
                const hopefuls = preferencesByPosition[index] || [];

                return (
                  <div key={player.playerId} className="tv-order-slot">
                    {/* 順番番号 */}
                    <div className="slot-number-wrap">
                      <div className="slot-number">{index + 1}</div>
                      <div className="slot-number-label">
                        {index === 0 ? '1st' : index === 1 ? '2nd' : index === 2 ? '3rd' : `${index + 1}th`}
                      </div>
                    </div>

                    {/* プレイヤーカード */}
                    <div className={`slot-player-card ${wantsThis ? 'matched' : ''} ${hasOk ? 'confirmed' : ''}`}>
                      <div className="slot-player-name">
                        {player.nickname}
                        {player.playerId === room.hostId && <span className="host-crown">👑</span>}
                      </div>
                      <div className={`slot-player-status ${hasOk ? 'ok' : vote?.preference !== undefined ? 'voted' : 'waiting'}`}>
                        {hasOk ? (
                          <span className="status-ok">✓ OK</span>
                        ) : vote?.preference !== undefined ? (
                          <span className="status-preference">
                            {vote.preference === null ? (
                              <><span className="pref-icon">🎲</span> おまかせ</>
                            ) : (
                              <><span className="pref-icon">🎯</span> {vote.preference + 1}番希望</>
                            )}
                          </span>
                        ) : (
                          <span className="status-waiting">考え中...</span>
                        )}
                      </div>
                    </div>

                    {/* この位置を希望している人（大きく目立つように表示） */}
                    <div className={`slot-hopefuls ${hopefuls.length > 0 ? 'has-hopefuls' : 'no-hopefuls'}`}>
                      {hopefuls.length > 0 ? (
                        <>
                          <span className="hopefuls-icon">🎯</span>
                          <span className="hopefuls-names">{hopefuls.join(', ')}</span>
                          <span className="hopefuls-suffix">が希望</span>
                        </>
                      ) : (
                        <span className="hopefuls-empty">希望者なし</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* OK状況バー */}
          <div className="tv-order-progress">
            <div className="progress-bar-wrap">
              <div
                className="progress-bar-fill"
                style={{ width: `${(okCount / totalPlayers) * 100}%` }}
              />
            </div>
            <div className="progress-text">
              <span className="ok-count">{okCount} / {totalPlayers}</span>
              <span className="ok-label">OK</span>
            </div>
            {okCount >= Math.ceil(totalPlayers / 2) && (
              <div className="can-start-badge">🚀 開始可能！</div>
            )}
          </div>

          <p className="tv-room-code">Room: {room.roomCode}</p>
        </div>
      </div>
    );
  }

  const currentPlayer = game.currentPlayerSlot !== null
    ? getPlayerBySlot(game.currentPlayerSlot, room.playerOrder, room.players)
    : null;

  // 結果画面
  if (game.phase === CHAIN_PHASE.RESULT || room.status === FL_ROOM_STATUS.FINISHED) {
    const isPerfect = game.correctCount === game.answerCount;
    return (
      <div className="chain-tv-container result-phase">
        <div className="chain-tv-result">
          <div className="result-header">
            <span className="result-icon">{isPerfect ? '🎉' : '💥'}</span>
            <h1 className="result-title">{isPerfect ? 'PERFECT!' : 'GAME OVER'}</h1>
          </div>
          <div className="result-score">
            <span className="score-value">{game.correctCount}</span>
            <span className="score-separator">/</span>
            <span className="score-total">{game.answerCount}</span>
          </div>
          <div className="result-answers">
            {game.submittedAnswers?.map((answer, i) => (
              <div key={i} className="result-answer">
                <span className="result-answer-num">{i + 1}</span>
                <span className="result-answer-text">{answer.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // タイムアウト
  if (game.phase === CHAIN_PHASE.TIMEOUT) {
    return (
      <div className="chain-tv-container timeout-phase">
        <div className="chain-tv-timeout">
          <span className="timeout-icon">💥</span>
          <h1 className="timeout-title">TIME UP!</h1>
          <p className="timeout-score">{game.correctCount} / {game.answerCount}</p>
        </div>
      </div>
    );
  }

  // 全問正解
  if (game.phase === CHAIN_PHASE.COMPLETE) {
    return (
      <div className="chain-tv-container complete-phase">
        <div className="chain-tv-complete">
          <span className="complete-icon">🎉</span>
          <h1 className="complete-title">PERFECT!</h1>
          <p className="complete-score">{game.answerCount}問 全問正解！</p>
        </div>
      </div>
    );
  }

  // 問題読み上げフェーズ
  if (game.phase === CHAIN_PHASE.READING) {
    return (
      <div className="chain-tv-container reading-phase">
        <div className="chain-tv-reading">
          <h1 className="reading-title">⚡ ANSWER CHAIN</h1>
          <div className="question-display reading">
            <p className="question-text">{game.prompt}</p>
          </div>
          <p className="reading-hint">問題を確認中...</p>
        </div>
      </div>
    );
  }

  // メインゲーム画面
  return (
    <div className="chain-tv-container game-phase">
      {/* 正解オーバーレイ */}
      {showCorrect && (
        <div className="tv-overlay correct-overlay">
          <span className="overlay-icon">⭕</span>
          <span className="overlay-text">正解！</span>
        </div>
      )}

      {/* 不正解オーバーレイ */}
      {showWrong && (
        <div className="tv-overlay wrong-overlay">
          <span className="overlay-icon">❌</span>
          <span className="overlay-text">
            {game.lastWrongReason === 'duplicate' ? 'かぶり！' : 'ブブー'}
          </span>
        </div>
      )}

      {/* ヘッダー */}
      <div className="chain-tv-header">
        <div className="tv-timer">
          <span className={`timer-num ${remainingTime <= 10 ? 'warning' : ''} ${remainingTime <= 5 ? 'critical' : ''}`}>
            {remainingTime}
          </span>
        </div>
        <div className="tv-title">⚡ ANSWER CHAIN</div>
        <div className="tv-progress">
          <span className="progress-num">{game.correctCount}</span>
          <span className="progress-sep">/</span>
          <span className="progress-total">{game.answerCount}</span>
        </div>
      </div>

      {/* 問題表示 */}
      <div className="question-display">
        <p className="question-text">{game.prompt}</p>
      </div>

      {/* 回答済みリスト */}
      <div className="tv-answers">
        {game.submittedAnswers?.map((answer, i) => (
          <div key={i} className="tv-answer">
            <span className="tv-answer-num">{i + 1}</span>
            <span className="tv-answer-text">{answer.text}</span>
          </div>
        ))}
      </div>

      {/* 現在のプレイヤー */}
      <div className="tv-current-player">
        <span className="current-label">回答者</span>
        <span className="current-name">{currentPlayer?.nickname || '???'}</span>
      </div>

      {/* プレイヤーリスト with 隕石 */}
      <div className="tv-player-arena">
        {controllers.map((player, i) => {
          const slot = room.playerOrder.indexOf(player.playerId);
          const isCurrent = slot === game.currentPlayerSlot;
          return (
            <div key={player.playerId} className={`tv-player-slot ${isCurrent ? 'current' : ''}`}>
              {/* 隕石（現在のプレイヤーの上にのみ表示） */}
              {isCurrent && game.phase === CHAIN_PHASE.ANSWERING && (
                <div
                  className={`meteor ${meteorPosition > 80 ? 'danger' : ''} ${meteorPosition > 95 ? 'critical' : ''}`}
                  style={{ top: `${meteorPosition}%` }}
                >
                  <span className="meteor-icon">☄️</span>
                  <div className="meteor-trail"></div>
                </div>
              )}
              {/* プレイヤーアバター */}
              <div className={`player-avatar ${isCurrent ? 'active' : ''}`}>
                <span className="avatar-emoji">😀</span>
              </div>
              {/* プレイヤー名 */}
              <div className="player-name-tag">
                {player.nickname}
                {isCurrent && <span className="turn-indicator">👈</span>}
              </div>
              {/* 地面/ターゲット */}
              <div className="player-ground"></div>
            </div>
          );
        })}
      </div>

      {/* 音声入力セクション */}
      {micEnabled && voiceSupported && (
        <div className="tv-voice-section">
          {/* 認識中テキスト表示 */}
          {voiceInterim && (
            <div className="tv-voice-interim">
              <span className="interim-label">認識中:</span>
              <span className="interim-text">{voiceInterim}</span>
            </div>
          )}

          {/* 音声入力コンポーネント */}
          <VoiceInput
            onResult={handleVoiceResult}
            onInterim={handleVoiceInterim}
            disabled={isSpeaking || submitting || game.phase !== CHAIN_PHASE.ANSWERING}
            autoStart={game.phase === CHAIN_PHASE.ANSWERING && !isSpeaking}
            keepListening={true}
            placeholder="回答を話してください..."
          />

          {/* ステータス表示 */}
          <div className="tv-voice-status">
            {isSpeaking ? (
              <span className="status-speaking">🔇 読み上げ中...</span>
            ) : game.phase === CHAIN_PHASE.ANSWERING ? (
              <span className="status-listening">🎤 音声認識中</span>
            ) : (
              <span className="status-waiting">⏸ 待機中</span>
            )}
          </div>

          {/* マイクOFFボタン */}
          <button
            className="tv-mic-toggle"
            onClick={() => setMicEnabled(false)}
          >
            マイクをOFFにする
          </button>
        </div>
      )}

      {/* マイクOFFの場合、ONボタンを表示 */}
      {!micEnabled && voiceSupported && game.phase === CHAIN_PHASE.ANSWERING && (
        <button
          className="tv-mic-enable-btn"
          onClick={() => setMicEnabled(true)}
        >
          🎤 マイクをONにする
        </button>
      )}
    </div>
  );
}

export default ChainTV;
